import { useLayoutEffect, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box, Button, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { UpdateAppSubHeaderTitle, ShowGlobalLoading, HideGlobalLoading } from '../../../redux/actions/UiActions';
import { confirmAccount } from '../../../api/ActivateAccount';
import FONTS_FAMILY from '../../../theme/FontsFamily';

function ActivateAccount() {
    const history = useHistory();
    const dispatch = useDispatch();
    const { authenticated } = useSelector((state) => state.authReducer);
    const { activationToken } = useParams();

    const [accountSuccessActivated, setAccountSuccessActivated] = useState(null);
    const [messageFromBackend, setMessageFromBackend] = useState('');

    const handleAccountActivation = async () => {
        dispatch(ShowGlobalLoading('Activando cuenta...'));
        let response = await confirmAccount(activationToken);
        if (response?.success) {
            setAccountSuccessActivated(true);
        } else {
            setAccountSuccessActivated(false);
        }
        setMessageFromBackend(response?.msg || '');
        dispatch(HideGlobalLoading());
    };

    useLayoutEffect(() => {
        dispatch(UpdateAppSubHeaderTitle(''));
    }, [dispatch]);

    useEffect(() => {
        if (authenticated) {
            history.push('/app/mi-escritorio');
            return;
        }
        handleAccountActivation();
    }, [authenticated]);

    return (
        <Box
            sx={{
                minHeight: '60vh',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                bgcolor: 'transparent',
                pt: 3,
                pb: 8,
                px: 2
            }}
        >
            <Box
                sx={{
                    maxWidth: 520,
                    width: '100%',
                    bgcolor: '#FFFFFF',
                    borderRadius: '24px',
                    boxShadow: '0 12px 40px rgba(15, 35, 80, 0.06)',
                    border: '1px solid #E1E9EC',
                    p: { xs: 4, sm: 6 },
                    textAlign: 'center'
                }}
            >
                {accountSuccessActivated === null ? (
                    <Box sx={{ py: 4 }}>
                        <CircularProgress sx={{ color: '#0F2350', mb: 3 }} size={48} />
                        <Typography sx={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '18px', color: '#0F2350' }}>
                            Verificando activación de cuenta...
                        </Typography>
                    </Box>
                ) : accountSuccessActivated ? (
                    <Box>
                        <Box
                            sx={{
                                width: 84,
                                height: 84,
                                borderRadius: '50%',
                                bgcolor: 'rgba(16, 185, 129, 0.1)',
                                color: '#10B981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                            }}
                        >
                            <CheckCircleOutlineIcon sx={{ fontSize: 52 }} />
                        </Box>
                        
                        <span className="uctt-eyebrow no-dash" style={{ color: '#0B8296', marginBottom: '8px' }}>
                            Verificación Completada
                        </span>

                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: 'Merriweather, Georgia, serif',
                                fontSize: { xs: '22px', sm: '26px' },
                                fontWeight: 700,
                                color: '#0F2350',
                                mt: 1,
                                mb: 2
                            }}
                        >
                            ¡Cuenta Activada Exitosamente!
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: FONTS_FAMILY.regular.body,
                                fontSize: '14.5px',
                                color: '#57697F',
                                lineHeight: 1.6,
                                mb: 4
                            }}
                        >
                            {messageFromBackend || 'Tu cuenta ha sido verificada correctamente. Ya puedes acceder al Portal de Trámites del Ministerio de Turismo para realizar tus solicitudes.'}
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={() => history.push('/login')}
                            fullWidth
                            sx={{
                                bgcolor: '#0F2350',
                                color: '#FFFFFF',
                                borderRadius: '12px',
                                py: 1.5,
                                fontSize: '15px',
                                fontWeight: 700,
                                fontFamily: FONTS_FAMILY.bold.body,
                                textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(15, 35, 80, 0.2)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#EA361A',
                                    boxShadow: '0 6px 18px rgba(234, 54, 26, 0.25)'
                                }
                            }}
                        >
                            Ingresar a mi cuenta
                        </Button>
                    </Box>
                ) : (
                    <Box>
                        <Box
                            sx={{
                                width: 84,
                                height: 84,
                                borderRadius: '50%',
                                bgcolor: 'rgba(239, 68, 68, 0.1)',
                                color: '#EF4444',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                            }}
                        >
                            <HighlightOffIcon sx={{ fontSize: 52 }} />
                        </Box>

                        <span className="uctt-eyebrow no-dash" style={{ color: '#EF4444', marginBottom: '8px' }}>
                            Error de Activación
                        </span>

                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: 'Merriweather, Georgia, serif',
                                fontSize: { xs: '22px', sm: '26px' },
                                fontWeight: 700,
                                color: '#0F2350',
                                mt: 1,
                                mb: 2
                            }}
                        >
                            No se pudo activar la cuenta
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: FONTS_FAMILY.regular.body,
                                fontSize: '14.5px',
                                color: '#57697F',
                                lineHeight: 1.6,
                                mb: 4
                            }}
                        >
                            {messageFromBackend || 'El enlace de activación ha expirado o no es válido. Si ya habías activado tu cuenta anteriormente, puedes intentar iniciar sesión directamente.'}
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={() => history.push('/login')}
                            fullWidth
                            sx={{
                                bgcolor: '#0F2350',
                                color: '#FFFFFF',
                                borderRadius: '12px',
                                py: 1.5,
                                fontSize: '15px',
                                fontWeight: 700,
                                fontFamily: FONTS_FAMILY.bold.body,
                                textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(15, 35, 80, 0.2)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#EA361A',
                                    boxShadow: '0 6px 18px rgba(234, 54, 26, 0.25)'
                                }
                            }}
                        >
                            Ir a Iniciar Sesión
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default ActivateAccount;